import type { CompensationLevel } from '@/types/compensation';

import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';

interface LevelIconProps {
    level: CompensationLevel;
    /** Box the mark is drawn in; the fallback dot centres itself inside the same box, so a level
     * whose icon cannot be resolved takes exactly as much room as one whose icon can. */
    className?: string;
}

/**
 * A performance level's mark, in the level's own colour.
 *
 * A level's `icon` is a key of the application's proficiency catalog (`trophy`, `medal`,
 * `checkCircle`…), not a name of the library's icon set — the same catalog the competency matrix
 * and the performance profile draw their level icons from, which is why this goes through
 * `getIconEntry`/`ProficiencyIcon` rather than the library's `Icon` (which renders nothing at all
 * for a name it does not know).
 *
 * When the key is not in the catalog — a company that configured its own icon file, a catalog that
 * has moved on — the level still has to be recognisable at a glance, so the colour is drawn on its
 * own as a dot. A level tile without its colour is just a paragraph.
 */
export const LevelIcon = ({ level, className = 'h-4 w-4' }: LevelIconProps) => {
    const iconEntry = level.icon ? getIconEntry(level.icon) : undefined;

    if (!iconEntry) {
        return (
            <span
                aria-hidden
                className={`${className} inline-flex items-center justify-center`}
                data-testid={`compensation-level-dot-${level.id}`}
            >
                <span
                    className={'h-2 w-2 rounded-full'}
                    style={{ backgroundColor: level.color }}
                />
            </span>
        );
    }

    return (
        <ProficiencyIcon
            iconEntry={iconEntry}
            color={level.color}
            className={className}
        />
    );
};
